import { Controller } from "stimulus"

// Admin-only Call brief / Company brief box on the campaign and company settings pages.
// Saves itself to PATCH /call_briefs a moment after typing stops, and on blur.
const LIMIT = 1000
const DEBOUNCE_MS = 800

interface SavedBrief {
  body: string
  updated_at: string
  updated_by_name: string | null
}

export default class CallBriefEditorController extends Controller {
  static values = { url: String, type: String, id: String }
  static targets = ["textarea", "count", "stamp", "error"]

  readonly urlValue!: string
  readonly typeValue!: string
  readonly idValue!: string
  readonly textareaTarget!: HTMLTextAreaElement
  readonly countTarget!: HTMLElement
  readonly stampTarget!: HTMLElement
  readonly errorTarget!: HTMLElement

  private saved = ""
  private timer: number | null = null
  private inFlight = false
  private again = false
  private readonly beforeUnload = (event: BeforeUnloadEvent): void => {
    if (!this.dirty() && !this.inFlight) return
    event.preventDefault()
    event.returnValue = ""
  }

  connect(): void {
    this.saved = this.textareaTarget.value
    this.count()
    window.addEventListener("beforeunload", this.beforeUnload)
  }

  disconnect(): void {
    this.clearTimer()
    window.removeEventListener("beforeunload", this.beforeUnload)
  }

  input(): void {
    this.count()
    this.errorTarget.textContent = ""
    if (this.dirty()) this.setStamp("Unsaved changes", "dirty")
    this.clearTimer()
    this.timer = window.setTimeout(() => this.save(), DEBOUNCE_MS)
  }

  saveNow(): void {
    this.clearTimer()
    this.save()
  }

  private save(): void {
    if (!this.dirty()) return
    if (this.inFlight) {
      this.again = true
      return
    }

    const body = this.textareaTarget.value
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
    this.inFlight = true
    this.setStamp("Saving…", "dirty")

    fetch(this.urlValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": token, Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ briefable_type: this.typeValue, briefable_id: this.idValue, body })
    })
      .then(response =>
        response
          .json()
          .catch(() => ({}))
          .then(data => (response.ok ? data : Promise.reject(data?.error || "The brief could not be saved.")))
      )
      .then((data: { brief: SavedBrief | null }) => {
        this.saved = body
        if (this.dirty()) {
          this.setStamp("Unsaved changes", "dirty")
        } else if (data.brief) {
          const name = data.brief.updated_by_name
          this.setStamp(`Updated just now${name ? ` · ${name}` : ""}`)
        } else {
          this.setStamp("No brief yet")
        }
      })
      .catch((error: unknown) => {
        this.setStamp("Not saved", "dirty")
        this.errorTarget.textContent = typeof error === "string" ? error : "The brief could not be saved. Check your connection and try again."
      })
      .finally(() => {
        this.inFlight = false
        if (this.again) {
          this.again = false
          this.save()
        }
      })
  }

  private dirty(): boolean {
    return this.textareaTarget.value !== this.saved
  }

  private count(): void {
    const length = this.textareaTarget.value.length
    this.countTarget.textContent = `${length} / ${LIMIT}`
    this.countTarget.classList.toggle("over", length > LIMIT * 0.9)
  }

  private setStamp(text: string, state: "dirty" | null = null): void {
    this.stampTarget.textContent = text
    this.stampTarget.classList.toggle("dirty", state === "dirty")
    this.stampTarget.classList.remove("stale")
  }

  private clearTimer(): void {
    if (this.timer === null) return
    window.clearTimeout(this.timer)
    this.timer = null
  }
}
